/**
 * Small typed fetch wrapper for the platform API.
 *
 * - Base URL per app (`VITE_API_BASE_URL`, deployed as `/admin/api` and `/webmail/api`); the
 *   spec's `/api` prefix is replaced by it, so `/api/health` → `/admin/api/health`.
 * - Cookies only (`credentials: 'same-origin'`): the session cookie `gx_<app>_sid` is HttpOnly.
 * - CSRF double submit: every non-GET sends `X-CSRF-Token` with the value of the readable cookie
 *   `gx_<app>_csrf` (fallback: the token from the last login/session response).
 * - Errors become `ApiError`; a 401 outside the login call notifies `onUnauthorized` listeners,
 *   which send the user back to the login screen.
 */
import { ApiError, CLIENT_ERROR_CODES, errorFromResponse } from './errors';
import type { ApiPath, AppName, MethodOf, PathParams, QueryParams, RequestBody, SuccessBody, UploadOptions } from './types';

export const CSRF_HEADER = 'X-CSRF-Token';

export const csrfCookieName = (app: AppName): string => `gx_${app}_csrf`;

/** Reads one cookie from `document.cookie` (undefined outside the browser). */
export function readCookie(name: string): string | undefined {
  if (typeof document === 'undefined') return undefined;
  for (const part of document.cookie.split(';')) {
    const index = part.indexOf('=');
    if (index < 0) continue;
    if (part.slice(0, index).trim() === name) return decodeURIComponent(part.slice(index + 1).trim());
  }
  return undefined;
}

type Scalar = string | number | boolean;
/** A query value; arrays repeat the key (`status=a&status=b`), empty values are left out. */
type QueryValue = Scalar | readonly Scalar[] | null | undefined;

export interface RequestOptions<P extends ApiPath, M extends MethodOf<P>> {
  path?: PathParams<P, M>;
  query?: QueryParams<P, M>;
  body?: RequestBody<P, M>;
  /** Extra request headers, e.g. `If-Match` for optimistic locking. */
  headers?: Readonly<Record<string, string>>;
  /** Do not notify `onUnauthorized` on 401 (login: 401 means wrong password). */
  ignoreUnauthorized?: boolean;
  signal?: AbortSignal;
}

export interface HttpClientOptions {
  app: AppName;
  /** e.g. `/admin/api`; trailing slashes are ignored. */
  baseUrl: string;
  /** Injected for tests; defaults to the global fetch. */
  fetch?: typeof fetch;
  /** Optional bearer credential for non-browser integrations. It is never persisted by the client. */
  token?: string;
  /** Query parameters added to every request (call options win), e.g. the console organisation filter. */
  extraQuery?: (method: string, path: string) => Readonly<Record<string, string>> | undefined;
}

export interface HttpClient {
  /** JSON extension endpoints with independently versioned contracts. Shares cookie, CSRF and error handling. */
  json<T>(method: string, path: string, body?: unknown, options?: { headers?: Record<string, string>; ignoreUnauthorized?: boolean }): Promise<T>;
  readonly app: AppName;
  request<P extends ApiPath, M extends MethodOf<P>>(method: M, path: P, options?: RequestOptions<P, M>): Promise<SuccessBody<P, M>>;
  /** Same as request, but returns the response as is (non-JSON bodies such as message/rfc822); errors as in request. */
  raw<P extends ApiPath, M extends MethodOf<P>>(method: M, path: P, options?: Omit<RequestOptions<P, M>, 'body'>): Promise<Response>;
  /** Absolute URL of an API path from the spec (`/api/v1/...` → `<base>/v1/...`, path params filled), e.g. for image sources. */
  url(path: string, params?: Readonly<Record<string, Scalar>>, query?: Readonly<Record<string, QueryValue>>): string;
  /**
   * POST of raw bytes (application/octet-stream) with upload progress (XMLHttpRequest: fetch
   * reports no upload progress). CSRF, error mapping and the 401 notification as in request.
   */
  upload(path: string, params: Readonly<Record<string, Scalar>>, query: Readonly<Record<string, Scalar>>, body: Blob, options?: UploadOptions): Promise<unknown>;
  /** Remembers the CSRF token of the current session (fallback when the cookie is not readable). */
  setCsrfToken(token: string | undefined): void;
  /** Subscribes to "session missing or expired"; returns the unsubscribe function. */
  onUnauthorized(listener: () => void): () => void;
}

function fillPath(path: string, params: Readonly<Record<string, Scalar>> | undefined): string {
  return path.replace(/\{(\w+)\}/g, (_, name: string) => {
    const value = params?.[name];
    if (value === undefined) throw new Error(`Missing path parameter "${name}" for ${path}`);
    return encodeURIComponent(String(value));
  });
}

function queryString(query: Readonly<Record<string, QueryValue>> | undefined): string {
  if (!query) return '';
  const params = new URLSearchParams();
  for (const [key, value] of Object.entries(query)) {
    const values: readonly (Scalar | null | undefined)[] = Array.isArray(value) ? value : [value as Scalar | null | undefined];
    for (const v of values) if (v !== undefined && v !== null && v !== '') params.append(key, String(v));
  }
  const text = params.toString();
  return text ? `?${text}` : '';
}

/** Untyped request options inside the client (the public methods are typed per route). */
interface SendOptions {
  path?: unknown;
  query?: unknown;
  body?: unknown;
  headers?: Record<string, string>;
  ignoreUnauthorized?: boolean;
  signal?: AbortSignal;
  accept: string;
}

export function createHttpClient({ app, baseUrl, fetch: fetchImpl, token, extraQuery }: HttpClientOptions): HttpClient {
  const base = baseUrl.replace(/\/+$/, '');
  const listeners = new Set<() => void>();
  let csrfToken: string | undefined;

  const doFetch = (input: string, init: RequestInit) => (fetchImpl ?? globalThis.fetch)(input, init);

  const csrfHeader = (): Record<string, string> => {
    const token = readCookie(csrfCookieName(app)) ?? csrfToken;
    return token ? { [CSRF_HEADER]: token } : {};
  };

  /** Maps a failed response to ApiError and notifies the 401 listeners. */
  const fail = async (response: Response): Promise<never> => {
    const error = await errorFromResponse(response);
    if (error.isUnauthorized || error.code === 'mfa_required') listeners.forEach((listener) => listener());
    throw error;
  };

  const urlFor = (path: string, params?: Readonly<Record<string, Scalar>>, query?: Readonly<Record<string, QueryValue>>): string =>
    `${base}${fillPath(path.replace(/^\/api/, ''), params)}${queryString(query)}`;

  /** fetch with CSRF, error mapping and the 401 notification; returns the successful response. */
  const send = async (method: string, path: string, options: SendOptions): Promise<Response> => {
    const verb = method.toUpperCase();
    const extra = extraQuery?.(verb, path);
    const query = extra ? { ...extra, ...(options.query as Record<string, QueryValue> | undefined) } : (options.query as Record<string, QueryValue> | undefined);
    const url = urlFor(path, options.path as Record<string, Scalar> | undefined, query);
    const headers: Record<string, string> = { Accept: options.accept, ...options.headers };
    if (options.body !== undefined) headers['Content-Type'] = 'application/json';
    if (token !== undefined) headers.Authorization = `Bearer ${token}`;
    else if (verb !== 'GET') Object.assign(headers, csrfHeader(), options.headers);
    let response: Response;
    try {
      response = await doFetch(url, {
        method: verb,
        headers,
        body: options.body === undefined ? undefined : JSON.stringify(options.body),
        credentials: token !== undefined ? 'omit' : 'same-origin',
        signal: options.signal,
      });
    } catch (cause) {
      if (cause instanceof DOMException && cause.name === 'AbortError') throw cause;
      throw new ApiError(0, CLIENT_ERROR_CODES.network, 'The server could not be reached.', { cause });
    }
    if (!response.ok) {
      const error = await errorFromResponse(response);
      if ((error.isUnauthorized || error.code === 'mfa_required') && !options.ignoreUnauthorized) listeners.forEach((listener) => listener());
      throw error;
    }
    return response;
  };

  const upload = (path: string, params: Readonly<Record<string, Scalar>>, query: Readonly<Record<string, Scalar>>, body: Blob, options: UploadOptions = {}): Promise<unknown> =>
    new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      xhr.open('POST', urlFor(path, params, query));
      xhr.withCredentials = token === undefined;
      xhr.setRequestHeader('Accept', 'application/json');
      xhr.setRequestHeader('Content-Type', 'application/octet-stream');
      if (token !== undefined) xhr.setRequestHeader('Authorization', `Bearer ${token}`);
      else for (const [name, value] of Object.entries(csrfHeader())) xhr.setRequestHeader(name, value);
      xhr.upload.onprogress = (event) => {
        if (event.lengthComputable) options.onProgress?.(event.loaded / event.total);
      };
      xhr.onload = () => {
        const response = new Response(xhr.responseText || null, { status: xhr.status, statusText: xhr.statusText, headers: { 'content-type': 'application/json' } });
        if (!response.ok) {
          fail(response).catch(reject);
          return;
        }
        response.json().then(resolve, (cause: unknown) => {
          reject(new ApiError(xhr.status, CLIENT_ERROR_CODES.invalidResponse, 'The server sent an invalid response.', { cause }));
        });
      };
      xhr.onerror = () => {
        reject(new ApiError(0, CLIENT_ERROR_CODES.network, 'The server could not be reached.'));
      };
      xhr.onabort = () => {
        reject(new DOMException('The upload was cancelled.', 'AbortError'));
      };
      options.signal?.addEventListener('abort', () => {
        xhr.abort();
      });
      xhr.send(body);
    });

  return {
    async json<T>(method: string, path: string, body?: unknown, options = {}) {
      const response = await send(method, path, { ...options, body, accept: 'application/json' });
      return await response.json() as T;
    },
    app,
    url: urlFor,
    upload,
    setCsrfToken(token) {
      csrfToken = token;
    },
    onUnauthorized(listener) {
      listeners.add(listener);
      return () => listeners.delete(listener);
    },
    raw: (method, path, options = {}) => send(method, path, { ...(options as Omit<SendOptions, 'accept'>), accept: '*/*' }),
    async request(method, path, options = {}) {
      const response = await send(method, path, { ...(options as Omit<SendOptions, 'accept'>), accept: 'application/json' });
      if (response.status === 204) return undefined as SuccessBody<typeof path, typeof method>;
      try {
        return (await response.json()) as SuccessBody<typeof path, typeof method>;
      } catch (cause) {
        throw new ApiError(response.status, CLIENT_ERROR_CODES.invalidResponse, 'The server sent an invalid response.', { cause });
      }
    },
  };
}
